import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, ListGroup, Row, Spinner, Stack } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa6';
import { useGetLicenseInfoQuery } from 'services/integrations/integrations.api';
import { fusionGetLicense, fusionVerifyLicense } from 'services/policy/license.service';

export const LicenseInformation = ({
  setShowLicenseInformation,
  inline,
  activationCode = '0',
  callback,
  fetchOnMount = true,
}) => {
  const [showKey, setShowKey] = useState(false);
  const [licenseDetails, setLicenseDetails] = useState();
  const [userInputKey, setUserInputKey] = useState('');
  const [validationError, setValidationError] = useState(false);
  const [fetching, setFetching] = useState(fetchOnMount);
  const [orgId, setOrgID] = useState('');
  const { data: licenseCounts = {} } = useGetLicenseInfoQuery(undefined, {
    skip: !fetchOnMount,
  });

  // Fetch existing license / activation info. Skipped in unauthenticated
  // contexts (e.g. account activation) since GET /license requires a session.
  useEffect(() => {
    if (!fetchOnMount) return;

    const fetchLicense = async () => {
      try {
        const res = await fusionGetLicense(activationCode);

        if (res.data.subscriptionInfo) {
          if (callback) callback();
          setLicenseDetails(res.data.subscriptionInfo[0]);
        } else if (res.data.activation) {
          setOrgID(res.data.activation[0].gen_wp_id);
        }
      } catch (err) {
        // optionally handle/log error
      } finally {
        setFetching(false);
      }
    };

    fetchLicense();
  }, [activationCode, callback, fetchOnMount]);

  const verifyLicense = async () => {
    if (userInputKey.length !== 29) return;

    try {
      setFetching(true);
      const res = await fusionVerifyLicense(userInputKey, activationCode);

      if (res.status === 200) {
        setValidationError(false);
        setLicenseDetails(res.data.subscriptionInfo[0]);
        if (callback) callback();
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMessage = err.response?.data?.message;

      if (status === 400 || status === 401 || status === 403) {
        setValidationError({ message: serverMessage || 'Invalid License Key' });
      } else if (serverMessage) {
        setValidationError({ message: serverMessage });
      } else {
        setValidationError({ message: 'Activation failed' });
      }
    } finally {
      setFetching(false);
    }
  };

  const stringToLicense = (str) => {
    // add hyphens every 5 chars
    let license = '';
    const cleaned = str.replace(/-/g, '');
    for (let i = 0; i < cleaned.length; i++) {
      if (i !== 0 && i % 5 === 0) license += '-';
      license += cleaned[i];
    }
    return license;
  };

  return (
    <Card className={inline ? 'border-0' : ''} style={{ backgroundColor: 'var(--color-base)' }}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">License Information</Card.Title>
        {!inline && (
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => setShowLicenseInformation(false)}
          >
            Ã—
          </Button>
        )}
      </Card.Header>

      <Card.Body>
        {fetching && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Fetching license details...</span>
          </div>
        )}

        {!fetching && !licenseDetails && (
          <Stack gap={3}>
            <Row>
              <Col xs={12} md="auto">
                <strong>Organization ID:</strong> <span className="text-muted">{orgId}</span>
              </Col>
            </Row>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                verifyLicense();
              }}
            >
              <Row className="align-items-center gy-2 gx-3">
                <Col xs={12} md="auto">
                  <Form.Label className="fw-semibold mb-0">License Key</Form.Label>
                </Col>

                <Col xs={12} md={6} lg={5}>
                  <Form.Control
                    type="text"
                    maxLength={29}
                    value={stringToLicense(userInputKey)}
                    onChange={(e) => setUserInputKey(e.target.value)}
                    className="font-monospace"
                    placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                  />
                </Col>

                <Col xs={12} md="auto">
                  <Button type="submit" variant="primary" disabled={userInputKey.length !== 29}>
                    Verify
                  </Button>
                </Col>
              </Row>
            </Form>

            {validationError && (
              <Alert variant="danger" className="mb-0">
                {validationError.message}
              </Alert>
            )}
          </Stack>
        )}

        {!fetching && licenseDetails && (
          <Stack gap={3} className="mt-3">
            <Row>
              <Col xs={12} md={8}>
                <div className="d-flex flex-wrap gap-1">
                  <span className="fw-semibold">Organization ID:</span>
                  <span className="text-muted">{licenseDetails.gen_wp_id}</span>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <span className="fw-semibold">License Key:</span>
                  <span className="text-muted">
                    {showKey ? licenseDetails.license_key : '*****-*****-*****-*****-*****'}
                  </span>
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="d-inline-flex align-items-center"
                    onClick={() => setShowKey((prev) => !prev)}
                  >
                    {showKey ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>

                <div className="d-flex flex-wrap gap-1 mt-2">
                  <span className="fw-semibold">License Expiration:</span>
                  <span className="text-muted">{licenseDetails.license_expiration}</span>
                </div>
              </Col>
            </Row>

            <div>
              <div className="fw-semibold mb-2">License Details</div>
              <ListGroup className="max-h-md max-w-lg">
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Base Fusion: {licenseDetails.fusion_activations} /{' '}
                  {licenseDetails.fusion_max_activations} licenses used
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Custom Weather: {licenseDetails.custom_weather ? 'Enabled' : 'Disabled'}
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Basic Systems: {licenseDetails.basic_system} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Advanced Systems: {licenseDetails.advanced_system} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Connected Devices: {licenseCounts.remaining_connected_devices} of{' '}
                  {licenseCounts.connected_devices} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Connected Cameras: {licenseCounts.remaining_connected_cameras} of{' '}
                  {licenseCounts.connected_cameras} available
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  SUSP: {licenseDetails.susp ? 'Enabled' : 'Disabled'}
                </ListGroup.Item>
                <ListGroup.Item style={{ backgroundColor: 'var(--color-primary)' }}>
                  Critical Mention: {licenseDetails.critical_mention} available
                </ListGroup.Item>
              </ListGroup>
            </div>
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
};
